import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { useWeb3 } from '../../context/Web3Context';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { formatUsdc, formatZenova, parseUsdc } from '../../utils/format';
import { ADDR, ZENOVA_DECIMALS } from '../../config/contracts';
import { sendTx } from '../../utils/txHelpers';
import { Alert } from '../common/StatBox';

export const TokenReserves = () => {
  const { account } = useWeb3();
  const { storageR, storageW, usdcR, zenovaR, zenovaW } = useContracts();
  const notify = useNotify();

  const [usdcBal, setUsdcBal]     = useState(null);
  const [zenovaBal, setZenovaBal] = useState(null);
  const [zenovaAllow, setZenovaAllow] = useState(null);

  const [depAmt, setDepAmt] = useState('');
  const [wdUsdc, setWdUsdc] = useState('');
  const [wdUsdcTo, setWdUsdcTo] = useState('');
  const [wdZen, setWdZen] = useState('');
  const [wdZenTo, setWdZenTo] = useState('');

  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!usdcR || !zenovaR) return;
      try {
        const [u, z] = await Promise.all([
          usdcR.balanceOf(ADDR.storage),
          zenovaR.balanceOf(ADDR.storage)
        ]);
        if (cancelled) return;
        setUsdcBal(u);
        setZenovaBal(z);
        if (account) {
          const a = await zenovaR.allowance(account, ADDR.storage);
          if (cancelled) return;
          setZenovaAllow(a);
        }
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [usdcR, zenovaR, account, reload]);

  // ============ Deposit ZENOVA ============
  const deposit = async () => {
    if (!depAmt || !zenovaW || !storageW) return;
    let amt;
    try { amt = ethers.utils.parseUnits(depAmt, ZENOVA_DECIMALS); } catch (e) {
      notify.error('Invalid amount'); return;
    }
    setBusy('dep');
    try {
      // Approve if needed
      if (!zenovaAllow || zenovaAllow.lt(amt)) {
        notify.info('Approving ZENOVA...');
        const ap = await zenovaW.approve(ADDR.storage, ethers.constants.MaxUint256);
        await ap.wait();
      }
      await sendTx(storageW, 'depositZenova', [amt], { notify, label: 'Deposit' });
      setDepAmt('');
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  // ============ Withdraw USDC ============
  const withdrawUsdc = async () => {
    if (!wdUsdc || !wdUsdcTo) return;
    let amt;
    try { amt = parseUsdc(wdUsdc); } catch (e) {
      notify.error('Invalid amount'); return;
    }
    // Pre-check contract balance
    if (usdcBal && amt.gt(usdcBal)) {
      notify.error(`Contract only holds $${formatUsdc(usdcBal)} USDC — cannot withdraw $${formatUsdc(amt)}`);
      return;
    }
    if (!ethers.utils.isAddress(wdUsdcTo)) {
      notify.error('Invalid recipient address'); return;
    }
    setBusy('wdu');
    try {
      await sendTx(storageW, 'ownerWithdrawUsdc', [wdUsdcTo, amt], { notify, label: 'Withdraw USDC' });
      setWdUsdc(''); setWdUsdcTo('');
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  // ============ Withdraw ZENOVA ============
  const withdrawZenova = async () => {
    if (!wdZen || !wdZenTo) return;
    let amt;
    try { amt = ethers.utils.parseUnits(wdZen, ZENOVA_DECIMALS); } catch (e) {
      notify.error('Invalid amount'); return;
    }
    // Pre-check contract balance
    if (zenovaBal && amt.gt(zenovaBal)) {
      notify.error(`Contract only holds ${formatZenova(zenovaBal)} ZENOVA — cannot withdraw ${formatZenova(amt)}`);
      return;
    }
    if (!ethers.utils.isAddress(wdZenTo)) {
      notify.error('Invalid recipient address'); return;
    }
    setBusy('wdz');
    try {
      await sendTx(storageW, 'ownerWithdrawZenova', [wdZenTo, amt], { notify, label: 'Withdraw ZENOVA' });
      setWdZen(''); setWdZenTo('');
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="card mb-24">
        <div className="section-title">Contract Reserves</div>
        <div className="admin-form-grid">
          <div className="admin-metric">
            <div className="metric-label">USDC (deposits)</div>
            <div className="metric-value">${formatUsdc(usdcBal)}</div>
          </div>
          <div className="admin-metric">
            <div className="metric-label">ZENOVA (payouts)</div>
            <div className="metric-value">{formatZenova(zenovaBal)}</div>
          </div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="section-title">Deposit ZENOVA Reserve</div>
        <div className="input-group">
          <label className="input-label">Amount (ZENOVA)</label>
          <input className="input" type="number" placeholder="10000" value={depAmt} onChange={(e) => setDepAmt(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={busy === 'dep' || !depAmt} onClick={deposit}>
          {busy === 'dep' && <ButtonSpinner />}Approve & Deposit
        </button>
      </div>

      <div className="card mb-24">
        <div className="section-title">Emergency Withdraw USDC</div>
        <Alert type="warning">Owner-only. Reduces user-claimable reserve — use with caution.</Alert>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Amount (USDC)</label>
            <input className="input" type="number" value={wdUsdc} onChange={(e) => setWdUsdc(e.target.value)} />
            {usdcBal && (
              <button
                type="button"
                onClick={() => setWdUsdc(ethers.utils.formatUnits(usdcBal, 6))}
                style={{ marginTop: 4, background: 'transparent', color: 'var(--neon-cyan)', fontSize: 11, padding: '2px 0', cursor: 'pointer' }}
              >
                Max: ${formatUsdc(usdcBal)}
              </button>
            )}
          </div>
          <div className="input-group">
            <label className="input-label">To Address</label>
            <input className="input" placeholder="0x..." value={wdUsdcTo} onChange={(e) => setWdUsdcTo(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-danger" disabled={busy === 'wdu' || !wdUsdc || !wdUsdcTo} onClick={withdrawUsdc}>
          {busy === 'wdu' && <ButtonSpinner />}Withdraw USDC
        </button>
      </div>

      <div className="card">
        <div className="section-title">Withdraw ZENOVA Reserve</div>
        <Alert type="warning">Owner-only. Depletes payout reserve — users won't be able to claim ZENOVA until refilled.</Alert>
        <div className="admin-form-grid">
          <div className="input-group">
            <label className="input-label">Amount (ZENOVA)</label>
            <input className="input" type="number" value={wdZen} onChange={(e) => setWdZen(e.target.value)} />
            {zenovaBal && (
              <button
                type="button"
                onClick={() => setWdZen(ethers.utils.formatUnits(zenovaBal, ZENOVA_DECIMALS))}
                style={{ marginTop: 4, background: 'transparent', color: 'var(--neon-cyan)', fontSize: 11, padding: '2px 0', cursor: 'pointer' }}
              >
                Max: {formatZenova(zenovaBal)}
              </button>
            )}
          </div>
          <div className="input-group">
            <label className="input-label">To Address</label>
            <input className="input" placeholder="0x..." value={wdZenTo} onChange={(e) => setWdZenTo(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-danger" disabled={busy === 'wdz' || !wdZen || !wdZenTo} onClick={withdrawZenova}>
          {busy === 'wdz' && <ButtonSpinner />}Withdraw ZENOVA
        </button>
      </div>
    </div>
  );
};
